import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
import { mockPricingPlans } from '../mock';
import { subscribeNewsletter, createSubscription } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Label } from './ui/label';
import ConfirmationDialog from './ConfirmationDialog';

const PricingSection = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language];
  const { toast } = useToast();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [showNewsletterConfirmation, setShowNewsletterConfirmation] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setNewsletterLoading(true);
    try {
      const result = await subscribeNewsletter(newsletterEmail, language);
      setShowNewsletterConfirmation(true);
      setNewsletterEmail('');
    } catch (error) {
      toast({
        title: t.error,
        description: error.message || (language === 'en' ? 'Something went wrong. Please try again.' : 'حدث خطأ ما. يرجى المحاولة مرة أخرى.'),
        variant: 'destructive',
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    setSubscriptionLoading(true);

    try {
      const result = await createSubscription({
        ...subscriptionData,
        plan: selectedPlan.id,
        planName: selectedPlan.name[language],
        price: selectedPlan.price,
        language
      });
      
      toast({
        title: t.success,
        description: result.message || (language === 'en' 
          ? 'Subscription created! Redirecting to payment...' 
          : 'تم إنشاء الاشتراك! جاري التوجيه للدفع...'),
      });
      
      // In production, redirect to payment URL
      // window.location.href = result.paymentUrl;
      
      setSelectedPlan(null);
      setSubscriptionData({ firstName: '', lastName: '', email: '', phone: '' });
    } catch (error) {
      toast({
        title: t.error,
        description: error.message || (language === 'en' ? 'Something went wrong. Please try again.' : 'حدث خطأ ما. يرجى المحاولة مرة أخرى.'),
        variant: 'destructive',
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-4">{t.joinClub}</h2>
        <p className="text-xl text-center text-gray-600 mb-16">{t.startFrom}</p>
        
        <div className="max-w-2xl mx-auto mb-16">
          {mockPricingPlans.filter(plan => plan.id === 'premium').map((plan) => (
            <div
              key={plan.id}
              className="rounded-xl p-10 bg-gradient-to-br from-purple-500 to-orange-500 text-white shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <div className="text-center mb-6">
                <div className="inline-block bg-white text-purple-500 text-xs font-bold px-4 py-2 rounded-full mb-3">
                  {t.mostPopular}
                </div>
                <h3 className="text-3xl font-bold mb-2">{plan.name[language]}</h3>
                <p className="text-purple-100 text-lg">
                  {plan.description[language]}
                </p>
              </div>
              
              <div className="text-center mb-8">
                <div className="mb-3">
                  <span className="text-6xl font-bold">{plan.price} OMR</span>
                  <span className="text-purple-200 text-xl ml-2">
                    {plan.period[language]}
                  </span>
                </div>
                <div className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full">
                  <span className="font-bold">
                    {language === 'en' ? '💰 Save 70% compared to monthly!' : '💰 وفر 70% مقارنة بالشهري!'}
                  </span>
                </div>
                <p className="text-purple-100 mt-2 text-sm">
                  {language === 'en' ? '(Only 2.9 OMR per month!)' : '(2.9 ريال فقط شهرياً!)'}
                </p>
              </div>
              
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    {feature.included ? (
                      <Check className="w-6 h-6 flex-shrink-0 text-white bg-green-500 rounded-full p-1" />
                    ) : (
                      <X className="w-6 h-6 flex-shrink-0 text-gray-400" />
                    )}
                    <span className={`text-lg ${!feature.included ? 'text-gray-400' : ''}`}>
                      {feature.text[language]}
                    </span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={() => navigate('/subscribe')}
                className="w-full font-bold py-6 rounded-lg text-lg bg-white text-purple-500 hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
              >
                {t.subscribeNow}
              </Button>
            </div>
          ))}
        </div>
        
        {/* Free Newsletter Section */}
        <div className="bg-gray-50 p-8 rounded-xl max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-6">{t.notReady}</h3>
          <p className="text-center mb-6 text-gray-700">{t.tryFree}</p>
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto flex gap-2">
            <Input
              type="email"
              placeholder={t.yourEmail}
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              className="flex-1"
              required
            />
            <Button 
              type="submit" 
              className="bg-orange-500 hover:bg-orange-600"
              disabled={newsletterLoading}
            >
              {newsletterLoading ? t.loading : t.subscribe}
            </Button>
          </form>
        </div>
      </div>

      {/* Subscription Dialog */}
      <Dialog open={!!selectedPlan} onOpenChange={() => setSelectedPlan(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'en' ? 'Subscribe to ' : 'الاشتراك في '}
              {selectedPlan?.name[language]}
            </DialogTitle>
            <DialogDescription>
              {language === 'en' 
                ? 'Fill in your details to continue to payment'
                : 'املأ بياناتك للمتابعة إلى الدفع'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubscriptionSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">{t.firstName}</Label>
                <Input
                  id="firstName"
                  value={subscriptionData.firstName}
                  onChange={(e) => setSubscriptionData({ ...subscriptionData, firstName: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">{t.lastName}</Label>
                <Input
                  id="lastName"
                  value={subscriptionData.lastName}
                  onChange={(e) => setSubscriptionData({ ...subscriptionData, lastName: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">{t.email}</Label>
              <Input
                id="email"
                type="email"
                value={subscriptionData.email}
                onChange={(e) => setSubscriptionData({ ...subscriptionData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">{t.whatsapp}</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+968 XXXX XXXX"
                value={subscriptionData.phone}
                onChange={(e) => setSubscriptionData({ ...subscriptionData, phone: e.target.value })}
                required
              />
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">{language === 'en' ? 'Total' : 'المجموع'}:</span>
                <span className="text-2xl font-bold text-orange-500">{selectedPlan?.price} OMR</span>
              </div>
              <p className="text-sm text-gray-600">
                {language === 'en' 
                  ? '**MOCK**: Payment gateway will be integrated in production' 
                  : '**تجريبي**: سيتم دمج بوابة الدفع في الإنتاج'}
              </p>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={subscriptionLoading}
            >
              {subscriptionLoading ? t.loading : (language === 'en' ? 'Continue to Payment' : 'المتابعة إلى الدفع')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Newsletter Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showNewsletterConfirmation}
        onClose={() => setShowNewsletterConfirmation(false)}
        type="newsletter"
      />
    </section>
  );
};

export default PricingSection;
